import { create } from 'zustand';
import { useEnhancedChatStore, EnhancedChatMessage } from './enhanced-store';
import { pitchChatApi } from 'src/utils/pitchChatApi';
import { Journey } from 'src/common/interfaces/journey.interface';
import { Organization } from 'src/common/interfaces/organization.interface';
import { Nullable } from 'src/types/nullable.type';
import { RenderMode } from '../types';
import { useCurrentSubscription } from 'src/store/subscription';

interface PitchChatState {
  // Pitch mode specific state
  isPitchMode: boolean;
  currentSectionId: string | null;
  currentStepId: string | null;
  
  // Settings state
  name: string;
  personality: string;
  avatarUrl: string;
  shouldGreetVisitors: boolean;
  disabled: boolean;
  
  // Navigation handlers
  onNavigateToSection: ((sectionId: string) => void) | null;
  onNavigateToStep: ((stepId: string) => void) | null;
  
  // Pitch mode specific methods
  setPitchMode: (isPitchMode: boolean) => void;
  setCurrentSection: (sectionId: string | null) => void;
  setCurrentStep: (stepId: string | null) => void;
  setNavigationHandlers: (handlers: {
    onNavigateToSection?: (sectionId: string) => void;
    onNavigateToStep?: (stepId: string) => void;
  }) => void;
  
  // Settings methods
  setName: (name: string) => void;
  setPersonality: (personality: string) => void;
  setShouldGreetVisitors: (shouldGreetVisitors: boolean) => void;
  setDisabled: (disabled: boolean) => void;
  initChat: (options?: { forceInit?: boolean }) => void;
  clearChat: () => void;
  
  // Initialize from journey data
  initializeFromJourney: (journey: Journey, organization?: Organization) => void;
  
  // Override enhanced chat methods for pitch mode
  startPitchConversation: (journeyUUID: string, visitorEmail?: string, userContext?: Record<string, any>) => Promise<void>;
  sendPitchMessage: (message: string) => Promise<void>;
  navigateToPitchContent: (contentUuid?: string, nodeUuid?: string) => Promise<void>;
}

export const usePitchChatStore = create<PitchChatState>((set, get) => ({
  // Pitch mode specific state
  isPitchMode: true,
  currentSectionId: null,
  currentStepId: null,
  
  // Settings state
  name: 'Journey Assistant',
  personality: '',
  avatarUrl: '',
  shouldGreetVisitors: false,
  disabled: true,
  
  // Navigation handlers
  onNavigateToSection: null,
  onNavigateToStep: null,
  
  // Pitch mode specific methods
  setPitchMode: (isPitchMode: boolean) => {
    set({ isPitchMode });
  },
  
  setCurrentSection: (sectionId: string | null) => {
    set({ currentSectionId: sectionId });
  },
  
  setCurrentStep: (stepId: string | null) => {
    set({ currentStepId: stepId });
  },
  
  setNavigationHandlers: (handlers) => {
    set({
      onNavigateToSection: handlers.onNavigateToSection || null,
      onNavigateToStep: handlers.onNavigateToStep || null,
    });
  },
  
  // Settings methods
  setName: (name: string) => {
    set({ name });
  },
  
  setPersonality: (personality: string) => {
    set({ personality });
  },
  
  setShouldGreetVisitors: (shouldGreetVisitors: boolean) => {
    set({ shouldGreetVisitors });
  },
  
  setDisabled: (disabled: boolean) => {
    set({ disabled });
  },
  
  initChat: (options?: { forceInit?: boolean }) => {
    // Initialize chat if needed
    const enhancedStore = useEnhancedChatStore.getState();
    if (options?.forceInit || !enhancedStore.isInitialized) {
      // This will be handled by the pitch conversation methods
    }
  },
  
  clearChat: () => {
    const enhancedStore = useEnhancedChatStore.getState();
    enhancedStore.endConversation();
  },
  
  // Initialize from journey data
  initializeFromJourney: (journey: Journey, organization?: Organization) => {
    const journeyData = journey.data || {};
    const talkToJourneySettings = journeyData['talk_to_journey'] || {};
    
    // Check if user has access to chatbot based on subscription plan
    const { subscriptionPlanType, isOnTrial } = useCurrentSubscription.getState();
    const hasChatbotAccess = (subscriptionPlanType === 'pro' || subscriptionPlanType === 'enterprise') || isOnTrial;
    
    // Only force-disable for new journeys (when talk_to_journey settings don't exist)
    // For existing journeys, respect the saved state from the backend
    const isNewJourney = !talkToJourneySettings || Object.keys(talkToJourneySettings).length === 0;
    const shouldDisableChatbot = isNewJourney && !hasChatbotAccess;
    const finalDisabledState = shouldDisableChatbot ? true : (talkToJourneySettings.disabled || false);
    
    set({
      name: talkToJourneySettings.name || 'Journey Assistant',
      personality: talkToJourneySettings.personality || '',
      shouldGreetVisitors: talkToJourneySettings.shouldGreetVisitors === true,
      disabled: finalDisabledState,
      avatarUrl: talkToJourneySettings.avatarUrl || '',
    });
  },
  
  // Override enhanced chat methods for pitch mode
  startPitchConversation: async (journeyUUID: string, visitorEmail?: string, userContext?: Record<string, any>) => {
    const state = get();
    const enhancedStore = useEnhancedChatStore.getState();
    
    // Check if already initializing or conversation exists
    if (enhancedStore.isInitializing || enhancedStore.conversationId) {
      return; // Already initializing or conversation exists
    }
    
    if (!state.isPitchMode) {
      // Fall back to enhanced chat for non-pitch mode
      return enhancedStore.startConversation(journeyUUID, visitorEmail, userContext);
    }
    
    // Set initializing state to true at the start
    useEnhancedChatStore.setState({ isInitializing: true, error: null });
    
    try {
      const result = await pitchChatApi.startConversation({
        journey_uuid: journeyUUID,
        visitor_email: visitorEmail,
        user_context: userContext,
      });
      
      if (result.success && result.data) {
        // Update the enhanced chat store with the conversation data
        const enhancedStore = useEnhancedChatStore.getState();
        enhancedStore.initializeFromJourney({ uuid: journeyUUID } as Journey);
        
        const { conversation_id, greeting, potential_questions, journey_context } = result.data;
        
        const greetingMessage: EnhancedChatMessage = {
          id: `greeting-${Date.now()}`,
          role: 'assistant',
          content: greeting.content,
          timestamp: new Date().toISOString(),
          metadata: {
            ...greeting.metadata,
          },
        };
        
        // Use the enhanced store's startConversation method but with pitch-specific data
        const enhancedStoreState = useEnhancedChatStore.getState();
        useEnhancedChatStore.setState({
          conversationId: conversation_id,
          messages: [greetingMessage],
          potentialQuestions: potential_questions || [],
          journeyContext: journey_context,
          journeyUUID,
          visitorEmail: visitorEmail || '',
          userContext: userContext || {},
          isInitialized: true,
          isInitializing: false,
          conversationMetrics: {
            messageCount: 1,
            lastUpdated: new Date().toISOString(),
          },
        });
      } else {
        throw new Error(result.error || 'Failed to start pitch conversation');
      }
    } catch (error) {
      console.error('Error starting pitch conversation:', error);
      // Set initializing to false on error
      useEnhancedChatStore.setState({ 
        isInitializing: false, 
        error: error instanceof Error ? error.message : 'Failed to start conversation' 
      });
      throw error;
    }
  },
  
  sendPitchMessage: async (message: string) => {
    const state = get();
    
    if (!state.isPitchMode) {
      // Fall back to enhanced chat for non-pitch mode
      const enhancedStore = useEnhancedChatStore.getState();
      return enhancedStore.sendMessage(message);
    }
    
    const enhancedStore = useEnhancedChatStore.getState();
    const { journeyUUID, visitorEmail, userContext, messages } = enhancedStore;
    
    if (!journeyUUID) {
      throw new Error('No journey UUID available');
    }
    
    // Add user message immediately
    const userMessage: EnhancedChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    
    useEnhancedChatStore.setState({
      messages: [...messages, userMessage],
      inputValue: '',
      isThinking: true,
      error: null,
    });
    
    try {
      const result = await pitchChatApi.sendMessage({
        journey_uuid: journeyUUID,
        message,
        visitor_email: visitorEmail,
        user_context: userContext,
      });
      
      if (result.success && result.data) {
        const {
          answer,
          source_content_uuid,
          source_node_uuid,
          confidence_score,
        } = result.data;
        
        const assistantMessage: EnhancedChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date().toISOString(),
          metadata: {
            source_content_uuid,
            source_node_uuid,
            confidence_score,
          },
        };
        
        const currentState = useEnhancedChatStore.getState();
        useEnhancedChatStore.setState({
          messages: [...currentState.messages, assistantMessage],
          isThinking: false,
        });
      } else {
        throw new Error(result.error || 'Failed to send pitch message');
      }
    } catch (error) {
      console.error('Error sending pitch message:', error);
      useEnhancedChatStore.setState({
        error: error instanceof Error ? error.message : 'Failed to send message',
        isThinking: false,
      });
    }
  },
  
  navigateToPitchContent: async (contentUuid?: string, nodeUuid?: string) => {
    const state = get();
    const enhancedStore = useEnhancedChatStore.getState();
    const { journeyUUID } = enhancedStore;
    
    if (!journeyUUID) {
      throw new Error('No journey UUID available');
    }
    
    try {
      const result = await pitchChatApi.navigateContent({
        journey_uuid: journeyUUID,
        content_uuid: contentUuid,
        node_uuid: nodeUuid,
      });
      
      if (result.success) {
        // Handle navigation based on what was provided
        if (nodeUuid && state.onNavigateToSection) {
          state.onNavigateToSection(nodeUuid);
        } else if (contentUuid && state.onNavigateToStep) {
          state.onNavigateToStep(contentUuid);
        }
      }
    } catch (error) {
      console.error('Error navigating pitch content:', error);
    }
  },
})); 